"use client";

import {
  createContext,
  useContext,
  useEffect,
  useState,
  useSyncExternalStore,
  type ReactNode,
} from "react";

import {
  appearanceKey as key,
  defaultPreferences as fallback,
  parsePreferences,
  type Preferences,
  type ThemeMode,
  type Density,
  type DesignPreset,
} from "./design-presets";
import type { TemplateId, Accent, Corners, FontChoice } from "./templates";
export type { ThemeMode, Density, DesignPreset } from "./design-presets";
const ThemeContext = createContext({
  ...fallback,
  templateLocked: false,
  resolved: "light" as "light" | "dark",
  setMode: (() => {}) as (mode: ThemeMode) => void,
  setDensity: (() => {}) as (density: Density) => void,
  setDesign: (() => {}) as (design: DesignPreset) => void,
  setTemplate: (() => {}) as (template: TemplateId) => void,
  setAccent: (() => {}) as (accent: Accent) => void,
  setCorners: (() => {}) as (corners: Corners) => void,
  setFont: (() => {}) as (font: FontChoice) => void,
});
const subscribeStorage = (notify: () => void) => {
  window.addEventListener("storage", notify);
  window.addEventListener("studio-theme", notify);
  return () => {
    window.removeEventListener("storage", notify);
    window.removeEventListener("studio-theme", notify);
  };
};
function readStorage() {
  try {
    return localStorage.getItem(key) ?? "";
  } catch {
    return "";
  }
}
const subscribeSystem = (notify: () => void) => {
  const query = window.matchMedia("(prefers-color-scheme: dark)");
  query.addEventListener("change", notify);
  return () => query.removeEventListener("change", notify);
};

export function ThemeProvider({
  children,
  mode,
  density,
  design,
  brand = "studio",
  persist = false,
  templateLocked = false,
  onModeChange,
  onDensityChange,
  onDesignChange,
  template,
  accent,
  corners,
  font,
  onTemplateChange,
  onAccentChange,
  onCornersChange,
  onFontChange,
}: {
  children: ReactNode;
  mode?: ThemeMode;
  density?: Density;
  design?: DesignPreset;
  brand?: "studio" | "neutral";
  persist?: boolean;
  templateLocked?: boolean;
  onModeChange?: (mode: ThemeMode) => void;
  onDensityChange?: (density: Density) => void;
  onDesignChange?: (design: DesignPreset) => void;
  template?: TemplateId;
  accent?: Accent;
  corners?: Corners;
  font?: FontChoice;
  onTemplateChange?: (value: TemplateId) => void;
  onAccentChange?: (value: Accent) => void;
  onCornersChange?: (value: Corners) => void;
  onFontChange?: (value: FontChoice) => void;
}) {
  const stored = useSyncExternalStore(subscribeStorage, readStorage, () => "");
  const systemDark = useSyncExternalStore(
    subscribeSystem,
    () => window.matchMedia("(prefers-color-scheme: dark)").matches,
    () => false,
  );
  const [local, setLocal] = useState<Preferences>(fallback);
  const preferences = persist && stored ? parsePreferences(stored) : local;
  const selectedMode = mode ?? preferences.mode;
  const selectedDensity = density ?? preferences.density;
  const selectedDesign = design ?? preferences.design;
  const current: Preferences = {
    mode: selectedMode,
    density: selectedDensity,
    design: selectedDesign,
    template: template ?? preferences.template,
    accent: accent ?? preferences.accent,
    corners: corners ?? preferences.corners,
    font: font ?? preferences.font,
  };
  const {
    template: selectedTemplate,
    accent: selectedAccent,
    corners: selectedCorners,
    font: selectedFont,
  } = current;
  const resolved =
    selectedMode === "system" ? (systemDark ? "dark" : "light") : selectedMode;
  useEffect(() => {
    const root = document.documentElement;
    root.dataset.theme = resolved;
    root.dataset.density = selectedDensity;
    root.dataset.brand = brand;
    root.dataset.design = selectedDesign;
    root.dataset.template = selectedTemplate;
    root.dataset.accent = selectedAccent;
    root.dataset.corners = selectedCorners;
    root.dataset.font = selectedFont;
    root.style.colorScheme = resolved;
  }, [
    resolved,
    selectedDensity,
    selectedDesign,
    brand,
    selectedTemplate,
    selectedAccent,
    selectedCorners,
    selectedFont,
  ]);
  function update(next: Preferences) {
    setLocal(next);
    if (persist) {
      try {
        localStorage.setItem(key, JSON.stringify(next));
        window.dispatchEvent(new Event("studio-theme"));
      } catch {
        /* Storage may be unavailable in private previews. */
      }
    }
  }
  return (
    <ThemeContext.Provider
      value={{
        ...current,
        templateLocked,
        mode: selectedMode,
        density: selectedDensity,
        design: selectedDesign,
        resolved,
        setMode: (next) => {
          update({ ...current, mode: next });
          onModeChange?.(next);
        },
        setDensity: (next) => {
          update({ ...current, density: next });
          onDensityChange?.(next);
        },
        setDesign: (next) => {
          update({ ...current, design: next });
          onDesignChange?.(next);
        },
        setTemplate: (next) => {
          update({ ...current, template: next });
          onTemplateChange?.(next);
        },
        setAccent: (next) => {
          update({ ...current, accent: next });
          onAccentChange?.(next);
        },
        setCorners: (next) => {
          update({ ...current, corners: next });
          onCornersChange?.(next);
        },
        setFont: (next) => {
          update({ ...current, font: next });
          onFontChange?.(next);
        },
      }}
    >
      {children}
    </ThemeContext.Provider>
  );
}
export const useTheme = () => useContext(ThemeContext);
